import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePurchaseStepStore from "~/store/purchase.store";
import { Check, DownloadIcon, Info, InfoIcon } from "lucide-react";
import networkService from "~/services/network.service";
import { handleError } from "~/shared/utils/errors.util";
import dayjs from "dayjs";
import { ReceiptIcon } from "~/shared/icon/receipt-icon";
import { EventRepeatIcon } from "~/shared/icon/event-repeat-icon";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import GenericValidationInput from "~/shared/components/generic-validation-input";
import { toast } from "react-toastify";
import { REGISTER_DEVICE, UPDATE_DEVICE_ON_LICENSE, GET_MANUFACTURER } from "~/shared/constants/api";
import iQ from "../../../public/iQ+-serial-number-location.png"
import iQ3 from "../../../public/iQ3-serial-number-location.png"
import useAuthStore from "~/store/auth.store";
import FullScreenModal from "~/shared/components/full-screen-modal";

// Debounce hook
const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const StepCounter = ({ step }: { step: number }) => {
    const steps = [
        { label: "Order" },
        { label: "Payment" },
        { label: "Confirmation" },
    ];
    return (
        <div className="items-center justify-center gap-8 mb-14 hidden md:flex">
            {steps.map((s, idx) => (
                <div key={s.label} className="flex items-center gap-2">
                    <div
                        className={`w-6 h-6 flex items-center justify-center rounded-md bg-gray-200 ${step === idx + 1 ? "text-white" : "border-gray-300 bg-white text-gray-500"}`}
                    >
                        {step > idx + 1 ? (
                            <Check size={16} className="text-[#807CD6] font-bold" />
                        ) : (
                            idx + 1
                        )}
                    </div>
                    <span className={`font-medium ml-2 ${step === idx + 1 ? "text-[#807CD6]" : "text-gray-500"}`}>{s.label}</span>
                </div>
            ))}
        </div>
    );
};

interface ProbeFormValues {
    probes: {
        serialNumber: string;
        name: string;
        model: string;
    }[];
}

const OrderConfirmation = () => {
    const { setStep } = usePurchaseStepStore();
    const navigate = useNavigate();
    const [licenseInfo, setLicenseInfo] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [loadingModels, setLoadingModels] = useState(false);
    const [searchParams] = useSearchParams();
    const [validationStatus, setValidationStatus] = useState<Record<number, boolean>>({});
    const { user } = useAuthStore((state) => ({ user: state.user }));
    const [showTooltip, setShowTooltip] = useState(false);
    const [selectedProbe, setSelectedProbe] = useState("iQ3");
    const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
    const [modelIds, setModelIds] = useState<{ iQ3: string; iQPlus: string }>({ iQ3: "", iQPlus: "" });

    // New state for serial number lookup
    const [serialNumberErrors, setSerialNumberErrors] = useState<Record<number, string>>({});
    const [lookupResults, setLookupResults] = useState<Record<number, { exists: boolean; deviceId?: string } | null>>({});
    const [isLookingUp, setIsLookingUp] = useState<Record<number, boolean>>({});

    // Move these hooks to the top level
    const [showFindItModalIndex, setShowFindItModalIndex] = useState<number | null>(null);
    const [referenceImageModel, setReferenceImageModel] = useState<'iQ3' | 'iQ+'>('iQ3');
    const [showFullScreen, setShowFullScreen] = useState(false);

    // Serial number validation rules
    const validationRules = [
        {
            rule: (value: string) => /^[A-Za-z]{2}/.test(value),
            message: "Must start with 2 letters",
        },
        {
            rule: (value: string) => value.length === 10,
            message: "Contain 10 characters",
        },
        {
            rule: (value: string) => !/[()]/.test(value),
            message: "Doesn't contain parentheses",
        },
    ];

    // Function to determine model based on serial number
    const getModelFromSerialNumber = (serialNumber: string) => {
        const prefix = serialNumber.substring(0, 2).toUpperCase();
        if (prefix === "BT") {
            return { modelId: modelIds.iQPlus, modelName: "iQ+" };
        } else if (prefix === "KB") {
            return { modelId: modelIds.iQ3, modelName: "iQ3" };
        } else {
            return { modelId: modelIds.iQ3, modelName: "iQ3" }; // Default to iQ3 for unknown prefixes
        }
    };

    // Serial number lookup function
    const lookupSerialNumber = useCallback(async (serialNumber: string, index: number) => {
        if (!serialNumber || serialNumber.length !== 10) {
            setLookupResults(prev => ({ ...prev, [index]: null }));
            setSerialNumberErrors(prev => ({ ...prev, [index]: "" }));
            return;
        }

        setIsLookingUp(prev => ({ ...prev, [index]: true }));
        try {
            const response = await networkService.get<any>(`/devices/${user.organizationId}/sn_lookup/${serialNumber}`);
            const deviceData = response.data;
            // If licenses array exists and is not empty, the probe is already linked
            if (deviceData.licenses && deviceData.licenses.length > 0) {
                setSerialNumberErrors(prev => ({
                    ...prev,
                    [index]: "This probe is already linked to an existing license. You must first remove this probe from the currently linked license."
                }));
                setLookupResults(prev => ({
                    ...prev,
                    [index]: { exists: true, deviceId: deviceData.id }
                }));
            } else {
                setSerialNumberErrors(prev => ({ ...prev, [index]: "" }));
                setLookupResults(prev => ({
                    ...prev,
                    [index]: { exists: false }
                }));
            }
        } catch (error: any) {
            // If 404, the device doesn't exist
            if (error.response?.status === 404 || error.message?.includes('not found')) {
                setSerialNumberErrors(prev => ({ ...prev, [index]: "" }));
                setLookupResults(prev => ({ ...prev, [index]: { exists: false } }));
            } else {
                setSerialNumberErrors(prev => ({ ...prev, [index]: "" }));
                setLookupResults(prev => ({ ...prev, [index]: null }));
            }
        } finally {
            setIsLookingUp(prev => ({ ...prev, [index]: false }));
        }
    }, [user.organizationId]);

    useEffect(() => {
        setStep(3);

        const fetchLicenseInfo = async () => {
            try {
                const subscriptionId = searchParams.get("subscriptionId");
                const checkoutSessionId = searchParams.get("sessionId");

                if (checkoutSessionId && subscriptionId) {
                    const response = await networkService.get<any>(
                        `/subscriptions/query-created-licenses/${checkoutSessionId}`
                    );
                    setLicenseInfo(response.data);

                    // Clear the stored IDs after successful fetch
                    localStorage.removeItem('checkoutSessionId');
                    localStorage.removeItem('subscriptionId');
                }
            } catch (error) {
                handleError({ error, message: (error as Error).message, level: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchLicenseInfo();
    }, [searchParams]);

    useEffect(() => {
        // Fetch model IDs for iQ3 and iQ+
        const fetchModelIds = async () => {
            setLoadingModels(true);
            try {
                const response = await networkService.get<any>(GET_MANUFACTURER);
                let iQ3Id = "";
                let iQPlusId = "";
                response.data.forEach((manufacturer: any) => {
                    manufacturer.models.forEach((model: any) => {
                        if (model.modelName.toLowerCase().includes("iq3")) iQ3Id = model.id;
                        if (model.modelName.toLowerCase().includes("iq+")) iQPlusId = model.id;
                    });
                });
                setModelIds({ iQ3: iQ3Id, iQPlus: iQPlusId });
            } catch (error) {
                handleError({ error, message: (error as Error).message, level: "error" });
            } finally {
                setLoadingModels(false);
            }
        };
        fetchModelIds();
    }, []);

    useEffect(() => {
        if (!licenseInfo) return;

        // Check if NOT a free trial
        const licenses = licenseInfo.licenses || [];
        const isTrial = licenses.some((lic: any) => lic.metaData?.isTrial);
        if (isTrial) return; // Don't fetch invoice for free trial

        // Get org and subscription IDs
        const orgId = licenses[0]?.subscription?.organization?.id;
        const subscriptionId = licenses[0]?.subscription?.id;

        if (orgId && subscriptionId) {
            // Fetch invoices for this org
            networkService.get(`/invoices/organization/${orgId}`)
                .then((res: any) => {
                    // Find the invoice for this subscription with totalAmount > 0
                    const invoice = res.data?.results?.find(
                        (inv: any) => inv.subscriptionId === subscriptionId && inv.totalAmount > 0 && inv.fileUrl
                    );
                    if (invoice) setInvoiceUrl(invoice.fileUrl);
                })
                .catch(() => setInvoiceUrl(null));
        }
    }, [licenseInfo]);

    const handleValidationChange = (index: number, isValid: boolean) => {
        setValidationStatus(prev => ({
            ...prev,
            [index]: isValid
        }));
    };

    const handleSubmit = async (values: ProbeFormValues) => {
        try {
            // Only consider probes where both serial number and name are filled
            const filledProbes = values.probes
                .map((probe, i) => ({ ...probe, licenseIndex: i }))
                .filter(probe => probe.serialNumber && probe.name);

            if (filledProbes.length === 0) {
                toast.error("Please provide probe information for at least one license.");
                return;
            }

            // Check if all filled serial numbers are valid
            const allValid = filledProbes.every((probe, idx) => validationStatus[probe.licenseIndex]);
            if (!allValid) {
                toast.error("Please correct the serial number format");
                return;
            }

            // Prepare payload for only filled probes
            const licenses = licenseInfo?.licenses || [];
            const results = [];

            for (let i = 0; i < filledProbes.length; i++) {
                const probe = filledProbes[i];
                const detectedModel = getModelFromSerialNumber(probe.serialNumber);
                let modelIdToUse = detectedModel.modelId;
                if (!modelIdToUse) {
                    modelIdToUse = modelIds.iQ3;
                }
                const payload = {
                    licenseId: licenses[probe.licenseIndex].id,
                    deviceParams: {
                        nickName: probe.name,
                        externalDeviceId: probe.serialNumber,
                        isActive: true,
                        modelId: modelIdToUse,
                    }
                };
                try {
                    // Try to register this individual probe
                    const response = await networkService.post(
                        `/devices/${user.organizationId}/bulk-register`,
                        [payload]
                    );
                    results.push({ success: true, probe: probe.serialNumber });
                } catch (error: any) {
                    if (
                        error?.response?.data?.message === "Device already registered" ||
                        error?.message === "Device already registered"
                    ) {
                        setSerialNumberErrors(prev => ({
                            ...prev,
                            [probe.licenseIndex]: "This probe is already linked to an existing license. You must first remove this probe from the currently linked license."
                        }));
                        results.push({ success: false, probe: probe.serialNumber, error: "already_registered" });
                    } else {
                        setSerialNumberErrors(prev => ({
                            ...prev,
                            [probe.licenseIndex]: error?.response?.data?.message || error?.message || "Failed to register probe"
                        }));
                        results.push({ success: false, probe: probe.serialNumber, error: "other" });
                    }
                }
            }

            // If at least one probe was registered, redirect
            const successfulRegistrations = results.filter(r => r.success).length;
            if (successfulRegistrations > 0) {
                toast.success("Probe(s) registered successfully!");
                navigate("/licenses");
            } else {
                toast.error("Failed to register any probes. Please check the error messages above.");
            }
        } catch (error: any) {
            handleError({ error, message: error.message, level: "error" });
        }
    };

    const handleSkip = () => {
        navigate("/licenses");
    };

    if (loading || loadingModels) {
        return <div className="flex flex-col items-center justify-center min-h-[60vh] py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#807CD6]"></div>
            <p className="mt-4 text-gray-600">Loading your order details...</p>
        </div>;
    }

    // Extract useful info
    const licenses = licenseInfo?.licenses || [];
    const isTrial = licenses.some((lic: any) => lic.metaData?.isTrial);
    const trialEndDate = isTrial ? licenses[0].metaData?.trialEndDate : null;
    const formattedTrialEnd = trialEndDate ? dayjs(trialEndDate).format('MMMM D, YYYY') : null;
    const licenseName = licenses[0]?.subscription?.plan?.name || "License";
    const billingCycle = licenses[0]?.subscription?.plan?.pricingModel?.intervalCount === 3 ? "3 years" : "1 year";
    const quantity = licenses.length;

    // Initialize form values based on license quantity
    const initialValues: ProbeFormValues = {
        probes: Array.from({ length: quantity }, () => ({
            serialNumber: "",
            name: "",
            model: "",
        }))
    };

    const probeEntrySchema = Yup.object().shape({
        serialNumber: Yup.string(),
        name: Yup.string(),
    }).test(
        'serial-or-empty',
        'Both serial number and name are required if either is filled',
        (value) => {
            // value is the object { serialNumber, name }
            if (!value) return true;
            const { serialNumber, name } = value;
            // If both are empty, it's valid
            if (!serialNumber && !name) return true;
            // If both are filled, it's valid
            if (serialNumber && name) return true;
            // Otherwise, invalid
            return false;
        }
    );

    const validationSchema = Yup.object({
        probes: Yup.array().of(probeEntrySchema)
    });

    return (
        <div className="flex flex-col min-h-screen font-space-grotesk">
            <div className="flex-grow py-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto w-full">
                <StepCounter step={3} />
                <h1 className="text-3xl font-bold mb-2 mt-2 text-center">Welcome aboard!</h1>
                <p className="text-lg text-gray-600 mb-8 text-center max-w-xl mx-auto">
                    Your payment was successful and your licenses are now active.
                </p>

                {/* Free Trial Banner */}
                {isTrial && (
                    <div className="w-full max-w-5xl mx-auto bg-indigo-50 border border-indigo-200 p-4 rounded-md text-indigo-700 text-sm mb-8">
                        <strong>Your Free Trial is now active until {formattedTrialEnd}.</strong>
                        <p className="text-indigo-600 mt-1">You will be automatically charged at the end of the Free Trial. We will send you a reminder before your trial ends.</p>
                    </div>
                )}

                <div className="block md:hidden border border-gray-300 rounded-md px-6 p-3 bg-white-a700 my-5">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-xl">Your order</h3>
                        {invoiceUrl && (
                            <a
                                href={invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 underline hover:text-black text-sm font-semibold"
                                download
                            >
                                <DownloadIcon size={18} />
                                Download invoice
                            </a>
                        )}
                    </div>
                    <div className="flex flex-col gap-2 text-[15px]">
                        <div className="flex items-center gap-3">
                            <span>
                                <ReceiptIcon />
                            </span>
                            <span>{licenseName} x {quantity}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span>
                                <EventRepeatIcon />
                            </span>
                            <span>{billingCycle} subscription</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 w-full max-w-5xl mx-auto">

                    {/* Left: Link Probes */}
                    <div className="w-full lg:w-3/5">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ values, setFieldValue }) => {
                                // Create individual debounced values for each probe
                                const debouncedSerialNumber0 = useDebounce(values.probes[0]?.serialNumber || '', 500);
                                const debouncedSerialNumber1 = useDebounce(values.probes[1]?.serialNumber || '', 500);
                                const debouncedSerialNumber2 = useDebounce(values.probes[2]?.serialNumber || '', 500);
                                const debouncedSerialNumber3 = useDebounce(values.probes[3]?.serialNumber || '', 500);
                                const debouncedSerialNumber4 = useDebounce(values.probes[4]?.serialNumber || '', 500);

                                // Trigger lookup when debounced serial numbers change
                                useEffect(() => {
                                    if (debouncedSerialNumber0 && debouncedSerialNumber0.length === 10) {
                                        lookupSerialNumber(debouncedSerialNumber0, 0);
                                    } else if (debouncedSerialNumber0 === '') {
                                        setLookupResults(prev => ({ ...prev, [0]: null }));
                                        setSerialNumberErrors(prev => ({ ...prev, [0]: "" }));
                                    }
                                }, [debouncedSerialNumber0, lookupSerialNumber]);

                                useEffect(() => {
                                    if (debouncedSerialNumber1 && debouncedSerialNumber1.length === 10) {
                                        lookupSerialNumber(debouncedSerialNumber1, 1);
                                    } else if (debouncedSerialNumber1 === '') {
                                        setLookupResults(prev => ({ ...prev, [1]: null }));
                                        setSerialNumberErrors(prev => ({ ...prev, [1]: "" }));
                                    }
                                }, [debouncedSerialNumber1, lookupSerialNumber]);

                                useEffect(() => {
                                    if (debouncedSerialNumber2 && debouncedSerialNumber2.length === 10) {
                                        lookupSerialNumber(debouncedSerialNumber2, 2);
                                    } else if (debouncedSerialNumber2 === '') {
                                        setLookupResults(prev => ({ ...prev, [2]: null }));
                                        setSerialNumberErrors(prev => ({ ...prev, [2]: "" }));
                                    }
                                }, [debouncedSerialNumber2, lookupSerialNumber]);

                                useEffect(() => {
                                    if (debouncedSerialNumber3 && debouncedSerialNumber3.length === 10) {
                                        lookupSerialNumber(debouncedSerialNumber3, 3);
                                    } else if (debouncedSerialNumber3 === '') {
                                        setLookupResults(prev => ({ ...prev, [3]: null }));
                                        setSerialNumberErrors(prev => ({ ...prev, [3]: "" }));
                                    }
                                }, [debouncedSerialNumber3, lookupSerialNumber]);

                                useEffect(() => {
                                    if (debouncedSerialNumber4 && debouncedSerialNumber4.length === 10) {
                                        lookupSerialNumber(debouncedSerialNumber4, 4);
                                    } else if (debouncedSerialNumber4 === '') {
                                        setLookupResults(prev => ({ ...prev, [4]: null }));
                                        setSerialNumberErrors(prev => ({ ...prev, [4]: "" }));
                                    }
                                }, [debouncedSerialNumber4, lookupSerialNumber]);

                                // Clear errors when serial numbers change
                                useEffect(() => {
                                    values.probes.forEach((probe, index) => {
                                        // Only clear the error if the serial number is empty
                                        if (serialNumberErrors[index] && !probe.serialNumber) {
                                            setSerialNumberErrors(prev => ({ ...prev, [index]: "" }));
                                        }
                                    });
                                }, [values.probes.map(p => p.serialNumber).join(','), serialNumberErrors]);

                                // Prefill probe names only if empty
                                useEffect(() => {
                                    values.probes.forEach((probe, i) => {
                                        if (!probe.name) {
                                            setFieldValue(`probes[${i}].name`, `probe-${i + 1}`);
                                        }
                                    });
                                    // Only run on mount or when quantity changes
                                    // eslint-disable-next-line
                                }, [setFieldValue, values.probes.length]);

                                return (
                                    <Form id="probe-form" className="bg-white border border-gray-300 rounded-md p-6">
                                        <h3 className="font-bold text-xl mb-4">Link your probes</h3>

                                        {/* Warning Banner */}
                                        <div className="bg-[#FDF5EF] border border-[#F3CCB5] rounded-md p-4 mb-6 text-red-700 text-sm flex gap-2">
                                            <InfoIcon size={16} className="mt-[2px]" />
                                            <p>You won't be able to use HeartFocus without adding your probe(s) serial number(s).</p>
                                        </div>

                                        {/* Dynamic Forms based on license quantity */}
                                        <div className="space-y-5">
                                            {values.probes.map((probe, index) => {
                                                const detectedModel = probe.serialNumber ? getModelFromSerialNumber(probe.serialNumber) : null;
                                                const hasError = serialNumberErrors[index];
                                                const isLookingUpProbe = isLookingUp[index];

                                                return (
                                                    <>
                                                        <div key={index} className="rounded-md">
                                                            <h4 className="ml-6 font-semibold text-sm mb-4 bg-[#F2EFEE] text-[#544B48] w-fit px-3 py-1 rounded-md">License {index + 1}</h4>

                                                            {hasError && (
                                                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md mb-4 font-medium flex">
                                                                    <span className="mr-2 mt-[3px]">
                                                                        <InfoIcon size={16} />
                                                                    </span>
                                                                    {hasError}
                                                                </div>
                                                            )}

                                                            <div className="flex md:justify-between justify-start flex-col md:flex-row">
                                                                <div className="mb-4">
                                                                    <div className="flex items-center justify-between">
                                                                        <label
                                                                            htmlFor={`probes.${index}.serialNumber`}
                                                                            className="block text-md font-bold leading-6 text-gray-900 pb-2"
                                                                        >
                                                                            Serial Number
                                                                        </label>
                                                                        {/* Find it button for mobile */}
                                                                        <button
                                                                            type="button"
                                                                            className="font-space-grotesk underline md:hidden pb-2"
                                                                            onClick={() => {
                                                                                setShowFindItModalIndex(index);
                                                                                setReferenceImageModel('iQ3');
                                                                            }}
                                                                        >
                                                                            Find it
                                                                        </button>
                                                                    </div>
                                                                    <GenericValidationInput
                                                                        setFieldValue={setFieldValue}
                                                                        onValidationChange={(isValid) => handleValidationChange(index, isValid)}
                                                                        name={`probes.${index}.serialNumber`}
                                                                        placeholder="ex: AB12458364"
                                                                        validationRules={validationRules}
                                                                        fieldClassName={hasError ? "border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]" : ""}
                                                                    />
                                                                    {/* Display lookup status */}
                                                                    {isLookingUpProbe && (
                                                                        <p className="text-xs mt-2 text-blue-600">
                                                                            Checking if probe exists...
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                <div>
                                                                    <label
                                                                        htmlFor={`probes.${index}.name`}
                                                                        className="block text-md font-bold leading-6 text-gray-900 pb-2"
                                                                    >
                                                                        Name
                                                                    </label>
                                                                    <Field
                                                                        id={`probes.${index}.name`}
                                                                        name={`probes.${index}.name`}
                                                                        type="text"
                                                                        placeholder="Enter Probe Name"
                                                                        className="font-space-grotesk px-4 py-3 w-full rounded-lg border-[1px] border-[#D0D0D6] placeholder-gray input-text focus:outline-none"
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`probes.${index}.name`}
                                                                        component="p"
                                                                        className="text-red-500 text-xs mt-2"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* Render separator between forms, but not after the last one */}
                                                        {index < values.probes.length - 1 && (
                                                            <div className="w-full border-t border-gray-200 my-2 pb-2" />
                                                        )}
                                                    </>
                                                );
                                            })}
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>

                    {/* Right: Order Summary */}
                    <div className="w-full lg:w-2/5 space-y-6">
                        <div className="hidden md:block border border-gray-300 rounded-md px-6 p-3 bg-white-a700">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-xl">Your order</h3>
                                {invoiceUrl && (
                                    <a
                                        href={invoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 underline hover:text-black text-sm font-semibold"
                                        download
                                    >
                                        <DownloadIcon size={18} />
                                        Download invoice
                                    </a>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 text-[15px]">
                                <div className="flex items-center gap-3">
                                    <span>
                                        <ReceiptIcon />
                                    </span>
                                    <span>{licenseName} x {quantity}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span>
                                        <EventRepeatIcon />
                                    </span>
                                    <span>{billingCycle} subscription</span>
                                </div>
                            </div>
                        </div>

                        <div className="border border-gray-300 rounded-md p-6 bg-[#F8F8FB] hidden md:block">
                            <div>
                                <h4 className="font-bold text-lg mb-3">Find your serial number</h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    The serial number is marked on the probe. It contains 10 characters, starting with letters.
                                </p>
                                {/* <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-blue-700 text-xs">
                                    <p><strong>Serial number prefixes:</strong></p>
                                    <p>• BT = iQ+ model</p>
                                    <p>• KB = iQ3 model</p>
                                    <p>• Other prefixes = Unknown probe</p>
                                </div> */}
                                {/* Toggle for visual reference only */}
                                <div className="flex justify-center">
                                    <div className="flex gap-2 mt-4 mb-4 bg-white-a700 rounded-full p-1 w-fit">
                                        <button
                                            type="button"
                                            className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${selectedProbe === "iQ3"
                                                ? "bg-[#AFAFE7] text-white shadow"
                                                : "bg-white text-gray-700"
                                                }`}
                                            onClick={() => setSelectedProbe("iQ3")}
                                        >
                                            iQ3
                                        </button>
                                        <button
                                            type="button"
                                            className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${selectedProbe === "iQ+"
                                                ? "bg-[#AFAFE7] text-white shadow"
                                                : "bg-white-a700 text-gray-700"
                                                }`}
                                            onClick={() => setSelectedProbe("iQ+")}
                                        >
                                            iQ+
                                        </button>
                                    </div>
                                </div>
                                {/* <p className="text-xs text-gray-500 text-center mb-2">
                                    (Toggle for visual reference only - model is detected from serial number)
                                </p> */}
                                {/* Image */}
                                <div className="mt-2">
                                    {selectedProbe === "iQ3" ? (
                                        <img
                                            src={iQ3}
                                            alt="iQ3 serial number location"
                                            className="w-full h-auto rounded-md"
                                        />
                                    ) : (
                                        <img
                                            src={iQ}
                                            alt="iQ+ serial number location"
                                            className="w-full h-auto rounded-md"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Footer */}
            <div className="sticky bottom-0 w-full bg-white-a700 border-t border-gray-200 md:p-4 px-6 py-4 flex md:justify-end justify-between items-center md:pr-96">
                <div className="relative">
                    <button
                        className="flex items-center gap-2 py-2 px-6 rounded-full underline border-gray-300 text-gray-700"
                        onClick={handleSkip}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        Later
                        <Info size={16} />
                    </button>

                    {showTooltip && (
                        <div className="absolute bottom-12 left-0 bg-gray-800 text-white-a700 text-sm p-3 rounded-md w-64 shadow-lg z-10">
                            You will need to link your probe(s) to access the HeartFocus app.
                        </div>
                    )}
                </div>

                <div className="justify-center items-center bg-angular-gradient rounded-full p-0.5">
                    <button
                        type="submit"
                        form="probe-form"
                        className="w-full font-semibold bg-white-a700 rounded-full px-9 py-2 hover:bg-light-angular-gradient"
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* Find It Modal (mobile, per probe) */}
            {showFindItModalIndex !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black-900 bg-opacity-40">
                    <div className="bg-white-a700 rounded-xl shadow-lg max-w-md w-full p-6 relative mx-5">
                        <button
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                            onClick={() => setShowFindItModalIndex(null)}
                            aria-label="Close"
                            type="button"
                        >
                            <span className="text-2xl">&times;</span>
                        </button>
                        <h2 className="text-xl font-bold mb-2 text-center">Probe Serial Number</h2>
                        <p className="text-gray-700 text-center mb-4">The serial number is marked on the probe. It contains 10 characters, starting with letters.</p>
                        <div className="flex gap-2 mb-4 bg-white-a700 rounded-full p-1 w-fit border border-gray-300 mx-auto">
                            <button
                                type="button"
                                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === 'iQ3' ? 'bg-[#AFAFE7] text-white shadow' : 'bg-white text-gray-700'}`}
                                onClick={() => setReferenceImageModel('iQ3')}
                            >
                                iQ3
                            </button>
                            <button
                                type="button"
                                className={`w-[100px] px-4 py-2 rounded-full font-semibold transition-all ${referenceImageModel === 'iQ+' ? 'bg-[#AFAFE7] text-white shadow' : 'bg-white-a700 text-gray-700'}`}
                                onClick={() => setReferenceImageModel('iQ+')}
                            >
                                iQ+
                            </button>
                        </div>
                        <div className="relative flex justify-center items-center bg-[#F5F5F7] h-[260px] w-full">
                            <img
                                src={referenceImageModel === 'iQ3' ? iQ3 : iQ}
                                alt={`${referenceImageModel} serial number location`}
                                className="h-auto rounded-md"
                            />
                            <button
                                className="absolute bottom-2 right-2 bg-[#C4C6EE] rounded-full p-2 shadow-md"
                                onClick={() => setShowFullScreen(true)}
                                type="button"
                                aria-label="Full screen"
                            >
                                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-16h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" /></svg>
                            </button>
                        </div>
                    </div>
                    {/* Full Screen Modal for the image */}
                    <FullScreenModal
                        images={[referenceImageModel === 'iQ3' ? iQ3 : iQ]}
                        isOpen={showFullScreen}
                        onClose={() => setShowFullScreen(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default OrderConfirmation;